// src/hooks/useUserManagement.ts
import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  addSubUser,
  updateSubUser,
  getSubUsers,
  deleteSubUser,
  getAllPermission,
  saveUserPermissions,
  getPermissions,
} from "../Api/UserManagementAPI";
import { StoreProptypes } from "../PropTypes/StoreProptypes";
import { LOADING, SET_MESSAGE } from "../Actions/Types";
import { useTranslation } from "react-i18next";
import { User, WebsiteAccess, Menu } from "../PropTypes/UserManagementTypes";

type CreateOrUpdateUserPayload = {
  name: string;
  email: string;
  // password fields are optional here because update may not need them
  password?: string;
  password_confirmation?: string;
};

export function useUserManagement() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { accessToken, websiteList } = useSelector(
    (store: StoreProptypes) => store
  );

  const [users, setUsers] = useState<User[]>([]);
  const [allPermission, setAllPermission] = useState<Menu[]>([]);
  const [loadingLocal, setLoadingLocal] = useState(false);

  const fetchSubUsers = useCallback(async () => {
    try {
      dispatch({ type: LOADING, payload: true });
      const res = await getSubUsers(accessToken || "");
      const data = res?.users || res?.data || [];
      setUsers(data);
    } catch (err: any) {
      console.error("fetchSubUsers error", err);
    } finally {
      dispatch({ type: LOADING, payload: false });
    }
  }, [accessToken, dispatch]);

  const fetchAllPermissions = useCallback(async () => {
    try {
      const res = await getAllPermission(accessToken || "");
      if (res?.status === "success") {
        setAllPermission(res?.data || []);
      }
    } catch (err) {
      console.error("fetchAllPermissions error", err);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    fetchSubUsers();
    fetchAllPermissions();
  }, [accessToken, fetchSubUsers, fetchAllPermissions]);

  const removeSubUser = useCallback(
    async (userId: number) => {
      try {
        dispatch({ type: LOADING, payload: true });
        await deleteSubUser(accessToken || "", userId);
        await fetchSubUsers();
        dispatch({
          type: SET_MESSAGE,
          payload: { toast_message: t("User deleted successfully!"), toast_type: "success" },
        });
      } catch (err: any) {
        dispatch({
          type: SET_MESSAGE,
          payload: { toast_message: err.message || t("Failed to delete user"), toast_type: "danger" },
        });
      } finally {
        dispatch({ type: LOADING, payload: false });
      }
    },
    [accessToken, dispatch, fetchSubUsers]
  );

  const saveUser = useCallback(
    async (payload: CreateOrUpdateUserPayload, editingUserId?: number, websites?: WebsiteAccess[]) => {
      dispatch({ type: LOADING, payload: true });
      setLoadingLocal(true);
      try {
        // 1. Add or update user
        let userResp;
        if (editingUserId) {
          userResp = await updateSubUser(accessToken || "", editingUserId, payload);
        } else {
          // addSubUser may require password fields depending on your API.
          // We allow optional password fields in the payload type so caller can include them if needed.
          userResp = await addSubUser(accessToken || "", payload as any);
        }

        if (userResp?.status !== 200 && userResp?.status !== "success") {
          const errorMsg = userResp?.errors?.email?.[0] || "Failed to save user";
          throw new Error(errorMsg);
        }

        const newUserId = userResp?.Data?.id || editingUserId;

        // 2. Ensure permissions list
        const permRes = await getAllPermission(accessToken || "");
        if (permRes?.status !== "success") throw new Error("Failed to fetch permissions");
        const allPermissionIds = permRes.data.map((p: any) => p.id);

        // 3. Build websites payload (strongly typed, no nulls)
        const websitesPayload: { website_id: number; permissions: number[] }[] =
          (websites && websites.length > 0)
            ? websites
                .map((site) => {
                  const websiteObj = websiteList.find((w: any) => w.domain === site.website);
                  if (!websiteObj) return null;
                  const permissions = site.sidebarMenus
                    .map((menuName: string) => permRes.data.find((p: any) => p.name === menuName)?.id)
                    .filter((id): id is number => Boolean(id));
                  return { website_id: websiteObj.id, permissions };
                })
                .filter((x): x is { website_id: number; permissions: number[] } => x !== null)
            : (websiteList || []).map((w: any) => ({ website_id: w.id, permissions: allPermissionIds }));

        // 4. Save permissions only for newly created user (existing updates handled elsewhere in UI)
        if (!editingUserId) {
          const savePermRes = await saveUserPermissions(accessToken || "", {
            user_id: newUserId,
            websites: websitesPayload,
          });
          if (savePermRes?.status !== "success") throw new Error("Failed to save user permissions");
        }

        await fetchSubUsers();

        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: editingUserId ? t("Sub-user updated successfully!") : t(`The sub-user account has been created successfully. A password reset link has been sent to the sub-user’s email address.`),
            toast_type: "success",
          },
        });

        return { success: true };
      } catch (err: any) {
        dispatch({
          type: SET_MESSAGE,
          payload: { toast_message: err.message || "Failed to save user", toast_type: "danger" },
        });
        return { success: false, message: err.message };
      } finally {
        dispatch({ type: LOADING, payload: false });
        setLoadingLocal(false);
      }
    },
    [accessToken, dispatch, websiteList, fetchSubUsers]
  );

  const fetchUserPermissions = useCallback(
    async (userId: number) => {
      try {
        const res = await getPermissions(accessToken || "", userId);
        return res?.data || [];
      } catch (err) {
        console.error("fetchUserPermissions", err);
        return [];
      }
    },
    [accessToken]
  );

  const savePermissionsForUser = useCallback(
    async (userId: number, selectedWebsitesAccess: WebsiteAccess[]) => {
      dispatch({ type: LOADING, payload: true });
      try {
        const websitesPayload: { website_id: number; permissions: number[] }[] = selectedWebsitesAccess
          .map((site) => {
            const websiteObj = websiteList.find((w: any) => w.domain === site.website);
            if (!websiteObj) return null;
            const permissions = site.sidebarMenus
              .map((menuName: string) => allPermission.find((p) => p.name === menuName)?.id)
              .filter((id): id is number => Boolean(id));
            return { website_id: websiteObj.id, permissions };
          })
          .filter((x): x is { website_id: number; permissions: number[] } => x !== null);

        const payload = { user_id: userId, websites: websitesPayload };
        const resp = await saveUserPermissions(accessToken || "", payload);
        if (resp?.status === "success") {
          await fetchSubUsers();
          dispatch({
            type: SET_MESSAGE,
            payload: { toast_message: t("Permissions saved successfully!"), toast_type: "success" },
          });
          return { success: true };
        } else {
          throw new Error(resp?.message || "Failed to save permissions");
        }
      } catch (err: any) {
        dispatch({
          type: SET_MESSAGE,
          payload: { toast_message: err.message || "Failed to save permissions", toast_type: "danger" },
        });
        return { success: false, message: err.message };
      } finally {
        dispatch({ type: LOADING, payload: false });
      }
    },
    [accessToken, allPermission, dispatch, fetchSubUsers, websiteList]
  );

  return {
    users,
    setUsers,
    fetchSubUsers,
    allPermission,
    setAllPermission,
    fetchAllPermissions,
    saveUser,
    removeSubUser,
    fetchUserPermissions,
    savePermissionsForUser,
    loadingLocal,
  };
}
